import { FC, ReactNode } from "react";
import { Link } from "react-router-dom";
import { clsx } from "clsx";
import { Breadcrumb, Layout, Spin } from "../../design-system";
import classes from "./VendorPage.module.less";

const { Header, Content } = Layout;

/** The list's location in the URL: the category (by key) the tree is rooted at, if any. */
export const CATEGORY_PARAM = "category";

/** The vendor list, rooted at a category when one is given. */
export const vendorPageHref = (categoryKey?: string | null) =>
    categoryKey == null
        ? "/vendor"
        : `/vendor?${new URLSearchParams({ [CATEGORY_PARAM]: categoryKey })}`;

/** The crumbs of a category and its ancestors, the ancestors as links. */
export const categoryCrumbs = (
    path: string[] | undefined,
    label: string,
    href: (categoryKey: string) => string
): VendorPageCrumb[] =>
    path
        ? path.map((crumb, i) => ({
              label: crumb,
              href: i < path.length - 1 ? href(path.slice(0, i + 1).join(" > ")) : undefined,
          }))
        : [{ label }];

export interface VendorPageCrumb {
    label: string;
    /** Where the crumb leads; the last crumb, the current page, has none. */
    href?: string;
}

interface VendorPageProps {
    /** The path under "Vendors" the page sits at; without it the page has no title header. */
    crumbs?: VendorPageCrumb[];
    /** The first crumb: "Vendors" at /vendor unless another section uses the frame. */
    root?: VendorPageCrumb;
    subtitle?: ReactNode;
    /** Buttons at the right of the title. */
    actions?: ReactNode;
    /** Shows a spinner in place of the content. */
    loading?: boolean;
    children?: ReactNode;
}

/**
 * The frame of the vendor pages: a breadcrumb for a title, from "Vendors"
 * down to the current place, a subtitle, actions on the right, then the
 * content.
 */
export const VendorPage: FC<VendorPageProps> = ({
    crumbs,
    root = { label: "Vendors", href: "/vendor" },
    subtitle,
    actions,
    loading = false,
    children,
}) => (
    <Layout className={classes.layout}>
        {crumbs && (
            <Header className={classes.header}>
                <div>
                    <Breadcrumb separator="›" className={classes.title}>
                        <Breadcrumb.Item>
                            {crumbs.length && root.href ? <Link to={root.href}>{root.label}</Link> : root.label}
                        </Breadcrumb.Item>
                        {crumbs.map((crumb, i) => (
                            <Breadcrumb.Item key={i}>
                                {crumb.href ? (
                                    <Link to={crumb.href}>{crumb.label}</Link>
                                ) : (
                                    crumb.label
                                )}
                            </Breadcrumb.Item>
                        ))}
                    </Breadcrumb>
                    {subtitle && <p className={classes.subtitle}>{subtitle}</p>}
                </div>
                {actions}
            </Header>
        )}
        <Content className={clsx(classes.content, !crumbs && classes["content--bare"])}>
            {loading ? (
                <div className={classes.loading}>
                    <Spin size="large" />
                </div>
            ) : (
                children
            )}
        </Content>
    </Layout>
);

VendorPage.displayName = "VendorPage";
